import * as Sentry from '@sentry/nextjs';
import axios, {
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  CreateAxiosDefaults,
  Method,
} from 'axios';
import axiosRetry, { isNetworkError } from 'axios-retry';
import { v4 } from 'uuid';

import appConfig from 'app-config';

import { DEBUG } from 'src/config';
import { isUseKeepAliveAtom } from 'src/network/atoms';
import store from 'src/stores';
import {
  remoteConfigAtom,
  turnstileTokenAtom,
  turnstileWidgetSiteKeyAtom,
  userCountryCodeAtom,
  userDataAtom,
} from 'src/stores/auth/atoms';
import {
  apiDelayAtom,
  apiDelayWhitelistAtom,
  compiledApiDelayPatternsAtom,
  globalApiDelayAtom,
  globalApiDelayEnabledAtom,
} from 'src/stores/debug/atom';
import { LoginWithPhoneThrowAble } from 'src/stores/phoneNumberLogin/types';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { isAxiosError } from 'src/utils/error';

import { isObjectKeyTrue } from './common';
import { REST_API_HOST, RPC_API_HOST } from './endpoint';
import { pushEvent } from './event-gateway';

export interface ErrorResponse<Data = unknown, Code = string> {
  error: {
    code: Code;
    data?: Data;
    message?: string;
  };
}

export interface ApiResponse<T> {
  code: number;
  result: T;
}

export interface Throwable {
  reason: string;
}

export interface RPCError<E = Throwable> {
  code: number;
  data: {
    throwable: E;
    exceptionTypeName: string;
    message?: string;
  };
  message: string;
}

export interface ErrorAlertMessage {
  alertBody: string;
  alertTitle: string;
  alertType: string;
  reason: string;
  linkButtonTitle?: string;
  linkButtonUrl?: string;
}

export interface PunishedException {
  type: 'WARNING' | 'LOGIN' | 'MATCH';
  userId: string;
  guidelineUrl: string;
  title: string;
  description: string;
  suspensionContent: string;
  additionalDescription?: string;
  appealUrl?: string;
}

export type RPCThrowableError = ErrorAlertMessage | PunishedException | LoginWithPhoneThrowAble;

export interface RPCResponse<T, E> {
  result: T;
  error?: RPCError<E>;
  jsonrpc: '2.0';
  id: number;
}

const headers = {
  'Content-Type': 'application/json',
  ...appConfig.headers,
  ...(process.env.NEXT_PUBLIC_RELEASE && {
    'X-Azar-Client-Version': `web/${process.env.NEXT_PUBLIC_RELEASE}`,
  }),
};

const timeout = 30_000;

export const getJsonRPCConfig = () => ({
  jsonrpc: '2.0',
  id: Math.ceil(Math.random() * 10_000_000),
});

const restApiConfig: CreateAxiosDefaults = {
  baseURL: REST_API_HOST,
  headers,
  withCredentials: false,
  timeout,
};

const fetchApiConfig: RequestInit = {
  credentials: 'omit',
  /* FIXME: signal 을 활성화하면 E2E 테스트에서 확정적으로 타임아웃 발생 */
  // signal: AbortSignal.timeout(timeout),
  headers,
};

export const rpcAxios = axios.create({
  baseURL: RPC_API_HOST,
  headers,
  withCredentials: true,
  timeout,
});

export const client = axios.create(restApiConfig);
export const clientWithoutAuth = axios.create(restApiConfig);

export const setClientLocale = (locale?: string) => {
  client.locale = locale;
};

export const setDTID = (DTID?: string) => {
  if (DTID) {
    Sentry.setTag('DTID', DTID);
    rpcAxios.defaults.headers.common = {
      ...rpcAxios.defaults.headers.common,
      'X-Azar-DTID': DTID,
    };
    client.defaults.headers.common = {
      ...client.defaults.headers.common,
      'X-Azar-DTID': DTID,
    };
  }
};

const retryConfig: Parameters<typeof axiosRetry>[1] = {
  retries: 3,
  retryDelay: (retryCount) => retryCount * 1000,
  retryCondition: (error) => {
    return axiosRetry.isNetworkOrIdempotentRequestError(error);
  },
};

axiosRetry(client, retryConfig);
axiosRetry(clientWithoutAuth, retryConfig);

const createAPICallMetaData = () => {
  return {
    startTime: Date.now(),
    traceId: v4(),
  } satisfies AxiosRequestConfig['metadata'];
};

const shouldLogAPICall = (response: AxiosResponse) => {
  const FALLBACK_API_CALL_LOG_ENABLED = true;
  const FALLBACK_API_CALL_LOG_EXCLUDE_PATHS =
    '/api/v1/match/face-exposure/reports,/api/v1/match/subtitles/languages,/api/v1/match/metrics/reports,/api/v1/webrtc/reports,/machine_learning/report/period/match,/machine_learning/report/summary/match,/machine_learning/report/metrics/match,/json/reportMatchMetrics,/json/reportMatchStart,/json/sendWebRTCStatInfoV2';

  const remoteConfig = store.get(remoteConfigAtom);
  if (!remoteConfig) return false;

  const isLogEnabled =
    remoteConfig.httpMetricsLoggingEnabled !== undefined
      ? isObjectKeyTrue(remoteConfig, 'httpMetricsLoggingEnabled')
      : FALLBACK_API_CALL_LOG_ENABLED;

  if (!isLogEnabled) return false;

  const { url, data } = response.config;

  /**
   * JSONRPC 요청은 url + method 조합으로 식별하기 때문에 맞춰서 포맷팅
   */
  const getFormattedPath = () => {
    if (url !== '/json') return url;

    try {
      const method = data ? `/${JSON.parse(data).method}` : '';
      return url + method;
    } catch {
      // 파싱 에러 고려 방어 로직
      return '';
    }
  };

  const formattedPath = getFormattedPath();

  const excludedPaths = (
    remoteConfig.httpMetricsExcludePaths ?? FALLBACK_API_CALL_LOG_EXCLUDE_PATHS
  ).split(',');

  return formattedPath && !excludedPaths.includes(formattedPath);
};

const sendSuccessfulAPIcallLog = (axiosClient: AxiosInstance, response: AxiosResponse) => {
  const endTimeMillis = Date.now();

  const { metadata } = response.config;
  if (!metadata) return;

  const url = axiosClient.getUri(response.config);

  pushEvent({
    eventType: EVENT_TYPE.DEBUG,
    eventName: EVENT_NAME.NETWORK_HTTP_REQUESTS,
    eventParams: {
      countryCode: store.get(userCountryCodeAtom),
      userId: store.get(userDataAtom)?.userId,
      url,
      method: response.config.method,
      statusCode: `${response.status}`,
      duration: endTimeMillis - metadata.startTime,
      startTimeMillis: metadata.startTime,
      endTimeMillis,
      traceId: metadata.traceId,
    },
  });
};

if (DEBUG) {
  client.interceptors.request.use(async (req) => {
    let totalDelay = 0;

    const globalEnabled = store.get(globalApiDelayEnabledAtom);
    const globalDelay = store.get(globalApiDelayAtom);

    if (globalEnabled && globalDelay > 0) {
      totalDelay += globalDelay;
    }

    const selectiveDelay = store.get(apiDelayAtom);
    if (selectiveDelay > 0) {
      const whitelist = store.get(apiDelayWhitelistAtom);
      const path = req.url || '';

      const shouldDelay =
        whitelist.restPaths.includes(path) ||
        store.get(compiledApiDelayPatternsAtom).some((regex) => regex.test(path));

      if (shouldDelay) {
        totalDelay += selectiveDelay;
      }
    }

    if (totalDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, totalDelay));
    }
    return req;
  });
}

client.interceptors.request.use((req) => {
  const userData = store.get(userDataAtom);

  req.metadata = createAPICallMetaData();

  const turnstileToken = store.get(turnstileTokenAtom);

  /**
   * axios에서 POST 요청에 payload 생략 시 Content-Type 헤더가 강제 제거됨
   * -> CORS 오류 발생을 막기 위해 명시적으로 null 넣어줌
   * @see https://github.com/axios/axios/issues/1535
   */
  if (req.method?.toLowerCase() === 'post' && req.data === undefined) {
    req.data = null;
  }
  req.headers = {
    ...req.headers,
    ...(userData?.accessToken && {
      Authorization: `Bearer ${userData?.accessToken}`,
    }),
    ...(turnstileToken && { 'X-Azar-TR': turnstileToken }),
  } as AxiosRequestHeaders;
  return req;
});

client.interceptors.response.use(
  (response) => {
    if (shouldLogAPICall(response)) {
      sendSuccessfulAPIcallLog(client, response);
    }
    return response;
  },
  (error) => {
    if (!isAxiosError<ErrorResponse<RPCThrowableError>>(error)) {
      return Promise.reject(error);
    }

    const requestUrl = client.getUri(error.config);
    const country = store.get(userCountryCodeAtom);

    if (isNetworkError(error)) {
      pushEvent({
        eventType: EVENT_TYPE.DEBUG,
        eventName: EVENT_NAME.GENERAL_CONNECTION_FAIL,
        eventParams: {
          generalConnectionFailInfo: error.message,
          generalConnectionFailType: 'API',
          country,
          generalConnectionAddress: requestUrl,
        },
      });
    } else if (error.response?.status && error.response.status >= 500) {
      pushEvent({
        eventType: EVENT_TYPE.DEBUG,
        eventName: EVENT_NAME.GENERAL_CONNECTION_FAIL,
        eventParams: {
          generalConnectionFailInfo: error.message,
          generalConnectionFailType: 'SERVER_INTERNAL',
          country,
          generalConnectionAddress: requestUrl,
        },
      });
    }

    if (error.response?.data?.error?.code === 'TURNSTILE_REQUIRED') {
      store.set(turnstileWidgetSiteKeyAtom, error.response.headers['x-azar-tk']);
    }
    return Promise.reject(error);
  }
);

if (DEBUG) {
  // API Delay Interceptor for RPC (Development only)
  rpcAxios.interceptors.request.use(async (req) => {
    let totalDelay = 0;

    const globalEnabled = store.get(globalApiDelayEnabledAtom);
    const globalDelay = store.get(globalApiDelayAtom);

    if (globalEnabled && globalDelay > 0) {
      totalDelay += globalDelay;
    }

    const selectiveDelay = store.get(apiDelayAtom);
    if (selectiveDelay > 0) {
      const whitelist = store.get(apiDelayWhitelistAtom);
      const method = req.data?.method || '';

      const shouldDelay = whitelist.rpcMethods.includes(method);

      if (shouldDelay) {
        totalDelay += selectiveDelay;
      }
    }

    if (totalDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, totalDelay));
    }
    return req;
  });
}

rpcAxios.interceptors.request.use((req) => {
  const userData = store.get(userDataAtom);

  req.metadata = createAPICallMetaData();
  /**
   * axios에서 POST 요청에 payload 생략 시 Content-Type 헤더가 강제 제거됨
   * -> CORS 오류 발생을 막기 위해 명시적으로 null 넣어줌
   * @see https://github.com/axios/axios/issues/1535
   */
  if (req.method?.toLowerCase() === 'post' && req.data === undefined) {
    req.data = null;
  }

  if (req.data.method === 'loginWithAccessToken') {
    req.headers = {
      ...req.headers,
      ...(userData?.accessToken && {
        Authorization: `Bearer ${userData?.accessToken}`,
      }),
    } as AxiosRequestHeaders;
  }

  return req;
});

rpcAxios.interceptors.response.use(
  (response) => {
    if (shouldLogAPICall(response)) {
      sendSuccessfulAPIcallLog(rpcAxios, response);
    }
    return response;
  },
  (error) => {
    if (!isAxiosError(error)) {
      return Promise.reject(error);
    }

    const requestUrl = client.getUri(error.config);
    const country = store.get(userCountryCodeAtom);

    if (isNetworkError(error)) {
      pushEvent({
        eventType: EVENT_TYPE.DEBUG,
        eventName: EVENT_NAME.GENERAL_CONNECTION_FAIL,
        eventParams: {
          generalConnectionFailInfo: error.message,
          generalConnectionFailType: 'API',
          country,
          generalConnectionAddress: requestUrl,
        },
      });
    } else if (error.response?.status && error.response.status >= 500) {
      pushEvent({
        eventType: EVENT_TYPE.DEBUG,
        eventName: EVENT_NAME.GENERAL_CONNECTION_FAIL,
        eventParams: {
          generalConnectionFailInfo: error.message,
          generalConnectionFailType: 'SERVER_INTERNAL',
          country,
          generalConnectionAddress: requestUrl,
        },
      });
    }

    return Promise.reject(error);
  }
);

export function createApiCall<TParams = void, TResponse = void>(
  method: Method,
  url: string,
  instance: AxiosInstance = client
) {
  return (params: TParams | null = null): AxiosPromise<ApiResponse<TResponse>> => {
    const config: AxiosRequestConfig = {
      method,
      url,
    };

    // GET 요청인 경우 params(=URL), POST 요청인 경우 body에 파라미터 추가
    if (method.toLowerCase() === 'get') {
      config.params = params;
    } else {
      config.data = params;
    }

    return instance(config);
  };
}

/**
 * 페이지 밖에서 생존 가능한 요청 날리기 위해 사용 (beforeunload event와 함께 사용 가능)
 * 주의) keepalive 옵션은 post와만 사용 가능
 */
export function createBeaconApiCall<TParams = void>(url: string) {
  return async (
    params: TParams,
    { headers: customHeaders }: Pick<RequestInit, 'headers'> = {}
  ): Promise<Response> => {
    const userData = store.get(userDataAtom);

    // axios는 페이지 밖에서 생존가능한 요청 날리는 keepalive 옵션 미지원 -> fetch로 대체
    const response = await fetch(`${REST_API_HOST}${url}`, {
      method: 'post',
      keepalive: store.get(isUseKeepAliveAtom),
      ...fetchApiConfig,
      ...(params && { body: JSON.stringify(params) }),
      headers: {
        ...fetchApiConfig.headers,
        ...customHeaders,
        ...(userData?.accessToken && {
          Authorization: `Bearer ${userData?.accessToken}`,
        }),
      },
    });

    return response;
  };
}

export const rpcClient =
  <Req = Record<string, unknown>, Res = Record<string, unknown>, E = Record<string, unknown>>(
    method: string,
    option?: AxiosRequestConfig
  ) =>
  (params?: Req) =>
    rpcAxios.post<RPCResponse<Res, E>>(
      '/json',
      {
        ...getJsonRPCConfig(),
        method,
        //params가 배열이면 배열로 넘기고, 아니면 객체로 넘김
        params: Array.isArray(params) ? params : params ? [{ ...params }] : undefined,
      },
      option
    );
